/**
 * Browser Locale Detection Utilities
 * Detects user's preferred language from browser settings
 * 
 * Optimized for Florida/Miami market where users may:
 * - Speak Spanish but prefer US date/currency formats
 * - Have mixed language preferences (es-US, en-US)
 */

import { type SupportedLocale, isValidLocale } from './localeStorage';

export const DEFAULT_LOCALE: SupportedLocale = 'en';

/**
 * Detects the preferred locale from the browser.
 * 
 * navigator.languages: Array ordered by user preference
 * navigator.language: Browser's primary language
 * 
 * Example navigator.languages: ['es-MX', 'es', 'en-US', 'en']
 * 
 * @returns The first supported language found, or DEFAULT_LOCALE
 */
export function detectBrowserLocale(): SupportedLocale {
  try {
    // Get browser languages array, fallback to single language
    const browserLocales = getBrowserLanguages();
    
    for (const browserLocale of browserLocales) {
      // Extract language code (es-MX -> es, en-US -> en)
      const langCode = extractLanguageCode(browserLocale);
      
      if (isValidLocale(langCode)) {
        return langCode;
      }
    }
  } catch {
    // navigator might not be available in some environments (SSR, tests)
    // eslint-disable-next-line no-console
    console.warn('Unable to detect browser locale');
  }
  
  return DEFAULT_LOCALE;
}

/**
 * Detects locale with region information (for date/number formatting).
 * 
 * Useful for Florida market where users may prefer:
 * - Language: Spanish
 * - Date/currency format: US (MM/DD/YYYY, $)
 * 
 * @returns Object with detected language, region, and full locale
 */
export function detectLocaleWithRegion(): {
  language: SupportedLocale;
  region: string | undefined;
  fullLocale: string;
} {
  try {
    const browserLocales = getBrowserLanguages();
    
    if (browserLocales.length === 0) {
      return {
        language: DEFAULT_LOCALE,
        region: undefined,
        fullLocale: DEFAULT_LOCALE,
      };
    }
    
    // Find first locale with a supported language
    for (const fullLocale of browserLocales) {
      const langCode = extractLanguageCode(fullLocale);
      
      if (isValidLocale(langCode)) {
        const region = extractRegionCode(fullLocale);
        return {
          language: langCode,
          region,
          fullLocale: region ? `${langCode}-${region}` : langCode,
        };
      }
    }
    
    // No supported language found, try to get region from first locale
    const firstLocale = browserLocales[0];
    const region = extractRegionCode(firstLocale);
    
    return {
      language: DEFAULT_LOCALE,
      region,
      fullLocale: region ? `${DEFAULT_LOCALE}-${region}` : DEFAULT_LOCALE,
    };
  } catch {
    // eslint-disable-next-line no-console
    console.warn('Unable to detect locale with region');
    return {
      language: DEFAULT_LOCALE,
      region: undefined,
      fullLocale: DEFAULT_LOCALE,
    };
  }
}

const FORMATTING_LOCALES: Record<SupportedLocale, string> = {
  en: 'en',
  es: 'es-US',
};

/**
 * Full locale for number, percent and currency formatting (react-intl).
 *
 * Fixed per language, never taken from the browser region (868mqhj74 CA-04):
 * Spanish always formats as es-US (Florida: "$1,234.50", "42.9%"), so a
 * browser in es-MX or es-ES gets the same output. English keeps 'en'.
 *
 * @param language - Language selected by the user
 * @returns Full locale for react-intl (e.g., 'es-US', 'en')
 */
export function getFormattingLocale(language: SupportedLocale): string {
  return FORMATTING_LOCALES[language] ?? language;
}

/**
 * Gets the detected region from browser settings.
 * Useful for formatting dates, numbers, and currency.
 * 
 * @returns Region code (e.g., 'US', 'MX', 'ES') or undefined
 */
export function getDetectedRegion(): string | undefined {
  try {
    const browserLocales = getBrowserLanguages();
    
    for (const locale of browserLocales) {
      const region = extractRegionCode(locale);
      if (region) {
        return region;
      }
    }
    
    return undefined;
  } catch {
    return undefined;
  }
}

// ============================================================================
// Internal Helper Functions
// ============================================================================

/**
 * Gets browser languages array with fallbacks.
 * Handles edge cases like IE11 and environments without navigator.
 */
function getBrowserLanguages(): readonly string[] {
  // Modern browsers
  if (typeof navigator !== 'undefined' && navigator.languages?.length > 0) {
    return navigator.languages;
  }
  
  // Fallback to single language
  if (typeof navigator !== 'undefined' && navigator.language) {
    return [navigator.language];
  }
  
  // No navigator available (SSR, some test environments)
  return [];
}

/**
 * Extracts language code from a locale string.
 * 
 * Examples:
 * - 'es-MX' -> 'es'
 * - 'en-US' -> 'en'
 * - 'es-419' -> 'es' (Latin America)
 * - 'es' -> 'es'
 * - 'ES' -> 'es' (normalized to lowercase)
 */
function extractLanguageCode(locale: string): string {
  if (!locale) {return '';}
  
  // Split by hyphen or underscore (some browsers use underscore)
  const parts = locale.split(/[-_]/);
  
  // Return lowercase language code
  return parts[0]?.toLowerCase() || '';
}

/**
 * Extracts region code from a locale string.
 * 
 * Examples:
 * - 'es-MX' -> 'MX'
 * - 'en-US' -> 'US'
 * - 'es-419' -> '419' (Latin America region code)
 * - 'es' -> undefined
 */
function extractRegionCode(locale: string): string | undefined {
  if (!locale) {return undefined;}
  
  // Split by hyphen or underscore
  const parts = locale.split(/[-_]/);
  
  if (parts.length < 2) {
    return undefined;
  }
  
  // Region is the second part, uppercase
  const region = parts[1]?.toUpperCase();
  
  // Validate it looks like a region (2 letters or 3 digits for UN M.49)
  if (region && (/^[A-Z]{2}$/.test(region) || /^\d{3}$/.test(region))) {
    return region;
  }
  
  return undefined;
}

/**
 * Checks if the browser appears to be in a US region.
 * Useful for defaulting to US formatting conventions.
 */
export function isUSRegion(): boolean {
  const region = getDetectedRegion();
  return region === 'US';
}
